// Minimaler Markdown-Renderer für EVE-Chat-Bubbles.
// Konvertiert das Markdown, das EVE produziert, in React-Elemente —
// ohne neue Dependency, ohne `dangerouslySetInnerHTML`.
//
// Unterstützt:
//   - **fett**, *kursiv*, `inline-code`
//   - # / ## / ### Header (max h6)
//   - Bullet-Listen mit `-`, `*` oder `•`
//   - Nummerierte Listen mit `1.` `2.` …
//   - > Blockquote
//   - --- / ━━━ / ─── Separator
//   - ```code block```
//   - Leerzeilen ⇒ neuer Absatz

import { Fragment, type ReactNode } from 'react';

const LINK_CLASS =
  'text-amber-300 underline underline-offset-2 hover:text-amber-200 break-words';

function anchor(href: string, label: string, key: number): ReactNode {
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
      {label}
    </a>
  );
}

function renderInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  // Reihenfolge im Alternativen-Block: Markdown-Link VOR nackter URL, sonst
  // frisst die URL-Alternative die Adresse aus `[text](url)`.
  //   [2] fett  [3] kursiv  [4] code
  //   [5] Link-Text + [6] Link-URL (Markdown-Link)
  //   [7] nackte http(s)-URL
  const regex =
    /(\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2] !== undefined) {
      parts.push(
        <strong key={key++} className="font-semibold text-amber-100">
          {match[2]}
        </strong>,
      );
    } else if (match[3] !== undefined) {
      parts.push(
        <em key={key++} className="italic">
          {match[3]}
        </em>,
      );
    } else if (match[4] !== undefined) {
      parts.push(
        <code
          key={key++}
          className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[13px] text-amber-200"
        >
          {match[4]}
        </code>,
      );
    } else if (match[6] !== undefined) {
      // Markdown-Link [Text](https://…)
      parts.push(anchor(match[6], match[5] ?? match[6], key++));
    } else if (match[7] !== undefined) {
      // Nackte URL — Satzzeichen am Ende NICHT mit verlinken (…besprechen: url.)
      let url = match[7];
      let trailing = '';
      const punct = /[.,;:!?)\]]+$/.exec(url);
      if (punct) {
        trailing = punct[0];
        url = url.slice(0, url.length - trailing.length);
      }
      parts.push(anchor(url, url, key++));
      if (trailing) parts.push(trailing);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length === 0 ? [text] : parts;
}

interface ListItem {
  ordered: boolean;
  text: string;
}

function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((c) => c.trim());
}

function flushList(
  blocks: ReactNode[],
  buffer: ListItem[],
  keyPrefix: string,
): void {
  if (buffer.length === 0) return;
  const ordered = buffer[0]!.ordered;
  const items = buffer.map((it, i) => (
    <li key={i} className="leading-relaxed">
      {renderInline(it.text)}
    </li>
  ));
  if (ordered) {
    blocks.push(
      <ol key={`${keyPrefix}-${blocks.length}`} className="my-1 ml-5 list-decimal space-y-0.5">
        {items}
      </ol>,
    );
  } else {
    blocks.push(
      <ul key={`${keyPrefix}-${blocks.length}`} className="my-1 ml-5 list-disc space-y-0.5">
        {items}
      </ul>,
    );
  }
  buffer.length = 0;
}

// EVE behauptet in ihrem Brain, sie haette Live-Search-Tools — die sind in V3
// aber global aus. Ohne echte Tools tippt sie gelegentlich die Anthropic-Tool-
// Syntax als Text in die Antwort. Diese versprengten Bloecke nie rendern.
// Greift auch mitten im Stream (ungeschlossener Block -> bis Ende strippen).
const TOOL_CALL_XML =
  /<function_calls>[\s\S]*?(?:<\/function_calls>|$)|<function_results>[\s\S]*?(?:<\/function_results>|$)|<\/?(?:invoke|parameter)\b[^>]*>/gi;

function stripToolCallXml(text: string): string {
  return text.replace(TOOL_CALL_XML, '').replace(/\n{3,}/g, '\n\n');
}

export function MarkdownText({ text }: { text: string }): ReactNode {
  if (!text) return null;

  const clean = stripToolCallXml(text);
  if (!clean.trim()) return null;

  const lines = clean.split('\n');
  const blocks: ReactNode[] = [];
  const listBuffer: ListItem[] = [];
  const codeBuffer: string[] = [];
  let inCode = false;

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i] ?? '';
    const line = raw;
    const trimmed = line.trim();

    if (inCode) {
      if (trimmed === '```') {
        blocks.push(
          <pre
            key={`pre-${blocks.length}`}
            className="my-2 overflow-x-auto rounded-md bg-black/40 p-3 font-mono text-[12px] text-neutral-200"
          >
            <code>{codeBuffer.join('\n')}</code>
          </pre>,
        );
        codeBuffer.length = 0;
        inCode = false;
      } else {
        codeBuffer.push(raw);
      }
      continue;
    }

    if (trimmed.startsWith('```')) {
      flushList(blocks, listBuffer, 'ul');
      inCode = true;
      continue;
    }

    // Separator: ━━━ ─── --- (mind. 3 Zeichen, nur diese)
    if (/^[━─\-]{3,}$/u.test(trimmed)) {
      flushList(blocks, listBuffer, 'ul');
      blocks.push(
        <hr
          key={`hr-${blocks.length}`}
          className="my-3 border-t border-amber-400/20"
        />,
      );
      continue;
    }

    // Header lines
    const hMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (hMatch) {
      flushList(blocks, listBuffer, 'ul');
      const level = hMatch[1]!.length;
      const content = hMatch[2]!;
      const className =
        level === 1
          ? 'mt-3 mb-1.5 text-lg font-bold text-amber-200'
          : level === 2
            ? 'mt-3 mb-1 text-base font-bold text-amber-200'
            : 'mt-2 mb-1 text-[15px] font-semibold text-neutral-100';
      const Tag = `h${Math.min(level, 6)}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      blocks.push(
        <Tag key={`h-${blocks.length}`} className={className}>
          {renderInline(content)}
        </Tag>,
      );
      continue;
    }

    // Ordered list: "1. text"
    const olMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
    if (olMatch) {
      if (listBuffer.length > 0 && !listBuffer[0]!.ordered) {
        flushList(blocks, listBuffer, 'ul');
      }
      listBuffer.push({ ordered: true, text: olMatch[2]! });
      continue;
    }

    // Unordered list item
    const ulMatch = trimmed.match(/^[-•*]\s+(.*)$/);
    if (ulMatch) {
      if (listBuffer.length > 0 && listBuffer[0]!.ordered) {
        flushList(blocks, listBuffer, 'ol');
      }
      listBuffer.push({ ordered: false, text: ulMatch[1]! });
      continue;
    }

    // Blockquote
    if (trimmed.startsWith('> ')) {
      flushList(blocks, listBuffer, 'ul');
      blocks.push(
        <blockquote
          key={`bq-${blocks.length}`}
          className="my-2 border-l-2 border-amber-400/40 pl-3 italic text-neutral-300"
        >
          {renderInline(trimmed.slice(2))}
        </blockquote>,
      );
      continue;
    }

    if (trimmed === '') {
      flushList(blocks, listBuffer, 'ul');
      continue;
    }

    // GFM-Table — beginnt mit `|...|`, gefolgt von Separator-Zeile `|---|---|`
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const nextRaw = lines[i + 1] ?? '';
      const nextTrim = nextRaw.trim();
      const isSeparator = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(nextTrim);
      if (isSeparator) {
        flushList(blocks, listBuffer, 'ul');
        const header = splitTableRow(trimmed);
        i++; // Separator-Zeile überspringen
        const rows: string[][] = [];
        while (i + 1 < lines.length) {
          const peek = (lines[i + 1] ?? '').trim();
          if (peek.startsWith('|') && peek.endsWith('|')) {
            rows.push(splitTableRow(peek));
            i++;
          } else {
            break;
          }
        }
        blocks.push(
          <div
            key={`tbl-${blocks.length}`}
            className="my-2 overflow-x-auto rounded-md border border-white/10"
          >
            <table className="min-w-full text-[13px] tabular">
              <thead className="bg-white/[0.04]">
                <tr>
                  {header.map((h, j) => (
                    <th
                      key={j}
                      className="border-b border-white/5 px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-amber-300"
                    >
                      {renderInline(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, j) => (
                  <tr key={j} className="border-b border-white/5 last:border-b-0">
                    {r.map((cell, k) => (
                      <td key={k} className="px-3 py-1.5 text-neutral-200">
                        {renderInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
        continue;
      }
    }

    // Default: Absatz. Wenn vorherige Zeile auch ein Absatz war, ans bestehende
    // <p> als Soft-Break hängen — sonst neues <p>.
    flushList(blocks, listBuffer, 'ul');
    blocks.push(
      <p key={`p-${blocks.length}`} className="leading-relaxed">
        {renderInline(line)}
      </p>,
    );
  }

  flushList(blocks, listBuffer, 'ul');
  if (inCode && codeBuffer.length > 0) {
    blocks.push(
      <pre
        key={`pre-${blocks.length}`}
        className="my-2 overflow-x-auto rounded-md bg-black/40 p-3 font-mono text-[12px] text-neutral-200"
      >
        <code>{codeBuffer.join('\n')}</code>
      </pre>,
    );
  }

  return <Fragment>{blocks}</Fragment>;
}
